import { DateTime } from "luxon";
import { immerable } from "immer";
import { LEGACY_TIME_ZONE } from "@/shared/utils/LegacyDates.ts";

/**
 * A time object used in schedules templates.
 */
export class ScheduleTemplateTime {
  [immerable] = true;
  constructor(
    public readonly hour = 0,
    public readonly minute = 0,
    public readonly day = 0,
  ) {
    if (day < -1 || day > 1) {
      throw new Error(`day ${day} is not in -1 to 1 range`);
    }
    if (hour < 0 || hour > 23) {
      throw new Error(`hour ${hour} is not in 0 to 23 range`);
    }
    if (minute < 0 || minute > 59) {
      throw new Error(`minute ${minute} is not in 0 to 59 range`);
    }
  }

  public sameValueAs(other: ScheduleTemplateTime): boolean {
    return (
      this.hour === other.hour &&
      this.minute === other.minute &&
      this.day === other.day
    );
  }

  /**
   * ScheduleTime is *always* expressed in legacy timezone.
   */
  public toDateTime(): DateTime {
    return DateTime.fromObject(
      {
        year: 1970,
        month: 1,
        day: 1,
        hour: this.hour,
        minute: this.minute,
        second: 0,
        millisecond: 0,
      },
      { zone: LEGACY_TIME_ZONE },
    ).plus({ days: this.day });
  }

  public formatTime(): string {
    const padded = (n: number): string => `${n}`.padStart(2, "0");
    return `${padded(this.hour)}:${padded(this.minute)}`;
  }

  public toMillis(): number {
    return this.toDateTime().toMillis();
  }

  public durationMinutes(): number {
    return this.minute + this.hour * 60 + this.day * 1440;
  }

  public toString(): string {
    return this.formatTime() + (this.day === 0 ? "" : `+${this.day}d`);
  }

  public static fromDate(date: Date): ScheduleTemplateTime {
    const dt = DateTime.fromJSDate(date, { zone: LEGACY_TIME_ZONE });
    return new ScheduleTemplateTime(dt.hour, dt.minute, 0);
  }

  public static correctedEndTime(
    start: ScheduleTemplateTime,
    end: ScheduleTemplateTime,
  ): ScheduleTemplateTime {
    if (
      start.hour < end.hour ||
      (start.hour === end.hour && start.minute < end.minute)
    ) {
      return new ScheduleTemplateTime(end.hour, end.minute, 0);
    }
    return new ScheduleTemplateTime(end.hour, end.minute, 1);
  }
}
